import { useState } from 'react';
import { Star } from 'lucide-react';
import { cn } from '../lib/utils';

export interface RatingStarsInputProps {
    value: number;
    onChange: (rating: number) => void;
    maxStars?: number;
    size?: 'sm' | 'md' | 'lg';
    className?: string;
    readOnly?: boolean;
}

export function RatingStarsInput({
    value,
    onChange,
    maxStars = 5,
    size = 'lg', // Larger by default for input
    className,
    readOnly = false
}: RatingStarsInputProps) {
    const [hoverRating, setHoverRating] = useState<number | null>(null);

    const sizeClasses = {
        sm: 'w-4 h-4',
        md: 'w-6 h-6',
        lg: 'w-8 h-8'
    };

    const handleMouseEnter = (index: number) => {
        if (!readOnly) setHoverRating(index + 1);
    };

    const handleMouseLeave = () => {
        if (!readOnly) setHoverRating(null);
    };

    const handleClick = (index: number) => {
        if (!readOnly) onChange(index + 1);
    };

    const displayRating = hoverRating !== null ? hoverRating : value;

    return (
        <div className={cn("flex items-center gap-1", className)} onMouseLeave={handleMouseLeave}>
            {[...Array(maxStars)].map((_, index) => (
                <button
                    key={index}
                    type="button"
                    className={cn(
                        "transition-transform hover:scale-110 focus:outline-none",
                        readOnly && "cursor-default hover:scale-100"
                    )}
                    onMouseEnter={() => handleMouseEnter(index)}
                    onClick={() => handleClick(index)}
                    disabled={readOnly}
                >
                    <Star
                        className={cn(
                            sizeClasses[size],
                            "transition-colors",
                            index < displayRating
                                ? "fill-yellow-400 text-yellow-400"
                                : "fill-transparent text-gray-300 hover:text-yellow-200"
                        )}
                        strokeWidth={1.5}
                    />
                </button>
            ))}
        </div>
    );
}
